import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/lib/axios';
import { Button } from '@/components/ui/button';
import Screen1Hook from '@/components/onboarding/Screen1Hook';
import Screen2Intent from '@/components/onboarding/Screen2Intent';
import Screen3Memory from '@/components/onboarding/Screen3Memory';
import Screen4Preview from '@/components/onboarding/Screen4Preview';
import Screen5Signup from '@/components/onboarding/Screen5Signup';
import { ONBOARDING_EVENTS, STORAGE_KEYS } from '@/constants/onboardingConfig';

const PREVIEW_FEEDBACK_OPTIONS = [
  { value: 'neutral', emoji: '😐', label: 'Meh' },
  { value: 'good',    emoji: '😊', label: 'Looks good' },
  { value: 'excited', emoji: '🤩', label: 'Love it' },
];

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [sessionId, setSessionId] = useState(null);
  const [intent, setIntent] = useState(null);
  const [audience, setAudience] = useState(null);
  const [memoryInput, setMemoryInput] = useState('');
  const [preview, setPreview] = useState(null);

  // Preview feedback overlay (shown between step 4 and 5)
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  // Initialize session ID once on mount
  useEffect(() => {
    let sid = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem(STORAGE_KEYS.SESSION_ID, sid);
    }
    setSessionId(sid);
  }, []);

  const trackEvent = useCallback(
    async (eventName, props = {}) => {
      if (!sessionId) return;
      try {
        await axios.post(`${API}/onboarding/track`, {
          event_name: eventName,
          session_id: sessionId,
          properties: props,
        });
      } catch {}
    },
    [sessionId],
  );

  // Fire started event once session is ready
  useEffect(() => {
    if (sessionId) trackEvent(ONBOARDING_EVENTS.STARTED);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePreviewReceived = useCallback((data) => {
    setPreview(data);
    localStorage.setItem(STORAGE_KEYS.PREVIEW, JSON.stringify(data));
  }, []);

  const handleStartOver = useCallback(() => {
    setMemoryInput('');
    setPreview(null);
    setIntent(null);
    setAudience(null);
    setFeedbackRating('');
    setShowFeedback(false);
    localStorage.removeItem(STORAGE_KEYS.PREVIEW);
    setStep(1);
  }, []);

  // Persist intent + audience to backend when moving from Screen 2 → 3
  const handleScreen2Next = useCallback(async () => {
    try {
      await axios.post(`${API}/onboarding/session`, {
        session_id: sessionId,
        intent,
        audience,
      });
    } catch {}
    setStep(3);
  }, [sessionId, intent, audience]);

  // After preview is seen, show the quick feedback overlay before signup
  const handlePreviewNext = useCallback(() => {
    setShowFeedback(true);
  }, []);

  const submitFeedbackAndContinue = useCallback(async (rating) => {
    setFeedbackSubmitting(true);
    try {
      if (rating) {
        await axios.post(`${API}/feedback`, {
          message: `Preview feedback: ${rating}`,
          type: 'onboarding_preview',
          rating,
        });
        trackEvent(ONBOARDING_EVENTS.FEEDBACK_SUBMITTED, { reason: rating });
      }
    } catch {}
    setFeedbackSubmitting(false);
    setShowFeedback(false);
    setStep(5);
  }, [trackEvent]);

  if (!sessionId) return null;

  const screenProps = {
    trackEvent,
    sessionId,
  };

  return (
    <>
      {step === 1 && (
        <Screen1Hook
          {...screenProps}
          onNext={() => setStep(2)}
          onSkip={() => setStep(5)}
        />
      )}
      {step === 2 && (
        <Screen2Intent
          {...screenProps}
          onNext={handleScreen2Next}
          onBack={() => setStep(1)}
          intent={intent}
          audience={audience}
          onIntentChange={setIntent}
          onAudienceChange={setAudience}
        />
      )}
      {step === 3 && (
        <Screen3Memory
          {...screenProps}
          onNext={() => setStep(4)}
          onBack={() => setStep(2)}
          memoryInput={memoryInput}
          onMemoryChange={setMemoryInput}
          intent={intent}
          audience={audience}
        />
      )}
      {step === 4 && !showFeedback && (
        <Screen4Preview
          {...screenProps}
          onNext={handlePreviewNext}
          onBack={handleStartOver}
          memoryInput={memoryInput}
          intent={intent}
          audience={audience}
          onPreviewReceived={handlePreviewReceived}
          preview={preview}
        />
      )}

      {/* Quick feedback overlay — appears after preview, before signup */}
      {step === 4 && showFeedback && (
        <div className="story-page flex flex-col items-center justify-center px-6 fade-in">
          <div className="max-w-sm w-full text-center">
            <p className="story-label mb-2">One quick question</p>
            <h2 className="story-title text-2xl mb-1">What did you think?</h2>
            <p className="story-meta mb-8">Your feedback helps us improve — takes 2 seconds.</p>

            <div className="flex gap-3 justify-center mb-8">
              {PREVIEW_FEEDBACK_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setFeedbackRating(opt.value === feedbackRating ? '' : opt.value)}
                  className={`flex flex-col items-center gap-1.5 px-5 py-4 rounded-2xl border transition-all duration-200 ${
                    feedbackRating === opt.value
                      ? 'border-[var(--story-primary)] bg-[var(--story-primary-soft)]'
                      : 'border-[var(--story-line-strong)] hover:border-[var(--story-primary)]/40'
                  }`}
                >
                  <span className="text-3xl leading-none">{opt.emoji}</span>
                  <span className="text-xs font-medium story-label">{opt.label}</span>
                </button>
              ))}
            </div>

            <Button
              onClick={() => submitFeedbackAndContinue(feedbackRating)}
              disabled={feedbackSubmitting}
              className="story-button-primary w-full mb-3"
            >
              {feedbackSubmitting ? 'Saving…' : 'Continue →'}
            </Button>
            <button
              onClick={() => submitFeedbackAndContinue('')}
              className="text-sm text-[#A09890] dark:text-[#5C5650] hover:text-[#6B6560] dark:hover:text-[#9C9590] transition-colors"
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {step === 5 && (
        <Screen5Signup
          {...screenProps}
          onBack={() => { setShowFeedback(false); setStep(4); }}
          preview={preview}
        />
      )}
    </>
  );
}
